/** Ties the three layers together: the useReducer state machine, a telephony
 *  provider (demo | livekit), and the CRM host bridge (Salesforce | postMessage |
 *  standalone). Telephony events flow one way into dispatch; UI actions call the
 *  provider then dispatch. Screen-pop fires on ring; the call log is written on end. */
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";

import { login as authLogin } from "../auth/authClient";
import { loadConfig } from "../config";
import { NoopHostBridge, type HostBridge } from "../host/hostBridge";
import { PostMessageHostBridge } from "../host/postMessageHost";
import { SalesforceHostBridge } from "../host/salesforceOpenCti";
import { DemoTelephonyProvider } from "../telephony/demoProvider";
import { LiveKitTelephonyProvider } from "../telephony/livekitProvider";
import type { Contact, TelephonyEvents, TelephonyProvider, TransferKind, TransferMode } from "../telephony/types";
import { callReducer, initialState, type AgentStatus, type CallState } from "./callReducer";
import { loadHistory, saveHistory } from "./historyStore";
import { loadSettings, saveSettings, type CtiSettings } from "./settingsStore";
import { ringtone } from "../softphone/ringtone";

interface CallApi {
  state: CallState;
  hostName: string;
  login(username: string, password: string): Promise<void>;
  logout(): void;
  dial(contact: Contact): Promise<void>;
  answer(): void;
  hangup(): void;
  toggleMute(): void;
  toggleHold(): void;
  toggleRecording(): void;
  transfer(target: string, kind: TransferKind, mode: TransferMode): void;
  conference(participant: string): void;
  sendDigit(digit: string): void;
  setNotes(notes: string): void;
  setDisposition(d: string): void;
  setAgentStatus(s: AgentStatus): void;
  settings: CtiSettings;
  setSetting<K extends keyof CtiSettings>(key: K, value: CtiSettings[K]): void;
  clear(): void;
}

const Ctx = createContext<CallApi | null>(null);

async function pickHost(): Promise<HostBridge> {
  if (await SalesforceHostBridge.isPresent()) return new SalesforceHostBridge();
  if (PostMessageHostBridge.isPresent()) return new PostMessageHostBridge();
  return new NoopHostBridge();
}

export function CallProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(callReducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const [settings, setSettings] = useState<CtiSettings>(loadSettings);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const providerRef = useRef<TelephonyProvider | null>(null);
  const hostRef = useRef<HostBridge | null>(null);
  const hostNameRef = useRef<string>("standalone");
  const cfgRef = useRef<Awaited<ReturnType<typeof loadConfig>> | null>(null);
  const wrapTimer = useRef<number | null>(null);

  const enrich = useCallback(async (number: string, direction: "inbound" | "outbound", interactionId?: string) => {
    const host = hostRef.current;
    if (!host) return;
    // Pass the call's interaction id so the CRM can also pull this call's transcript
    // + summary (server-side) — key for a bot→human transfer: the agent sees context.
    const res = await host.screenPop(number, direction, interactionId);
    if (res) dispatch({ type: "ENRICH_CONTACT", patch: { name: res.name, company: res.company, recordId: res.recordId, recordUrl: res.recordUrl } });
  }, []);

  // Telephony events → dispatch (one-way). Shared by whichever provider is active.
  const buildEvents = useCallback((): TelephonyEvents => ({
    onIncoming: (contact, callId) => {
      dispatch({ type: "INCOMING", callId, contact, atMs: Date.now() });
      hostRef.current?.setSoftphoneVisible(true);
      hostRef.current?.notifyCallState?.("ringing", { callId, number: contact.number });
      void enrich(contact.number, "inbound", callId);
    },
    onOutgoing: (contact, callId) => {
      dispatch({ type: "DIAL_START", callId, contact, atMs: Date.now() });
      hostRef.current?.setSoftphoneVisible(true);
      hostRef.current?.notifyCallState?.("ringing", { callId, number: contact.number });
      void enrich(contact.number, "outbound", callId);
    },
    onRinging: (callId) => dispatch({ type: "RINGING", callId }),
    onConnected: (callId) => {
      dispatch({ type: "CONNECTED", callId, atMs: Date.now() });
      // Answered → tell the host to minimise + start the call timer.
      hostRef.current?.notifyCallState?.("connected", { callId, startedAtMs: Date.now() });
    },
    onEnded: (callId, outcome) => {
      const call = stateRef.current.call;
      dispatch({ type: "ENDED", callId, outcome, atMs: Date.now() });
      hostRef.current?.notifyCallState?.("ended", { callId });
      if (call && call.id === callId) {
        void hostRef.current?.saveCallLog({
          direction: call.direction, number: call.contact.number, name: call.contact.name,
          recordId: call.contact.recordId, interactionId: call.id,
          durationSec: call.connectedAtMs ? Math.round((Date.now() - call.connectedAtMs) / 1000) : 0,
          outcome, startedAtMs: call.startedAtMs, notes: call.notes, disposition: call.disposition,
        });
      }
      wrapTimer.current = window.setTimeout(() => dispatch({ type: "CLEAR" }), 4000);
    },
    onError: (message) => dispatch({ type: "SET_ERROR", message }),
  }), [enrich]);

  const doDial = useCallback(async (contact: Contact) => {
    const provider = providerRef.current;
    if (!provider) return;   // not signed in yet
    if (wrapTimer.current) { clearTimeout(wrapTimer.current); dispatch({ type: "CLEAR" }); }
    hostRef.current?.setSoftphoneVisible(true);
    // The outbound call is created from the provider's onOutgoing event (symmetric
    // with inbound onIncoming), so both directions flow through one path.
    try { await provider.dial(contact); }
    catch (e) { dispatch({ type: "SET_ERROR", message: e instanceof Error ? e.message : "dial failed" }); }
  }, []);

  // Mount: wire the CRM host bridge (click-to-dial can arrive any time). The
  // telephony provider is created on LOGIN, with the signed-in agent's identity.
  useEffect(() => {
    let disposed = false;
    (async () => {
      cfgRef.current = await loadConfig();
      const host = await pickHost();
      if (disposed) { host.dispose(); return; }
      hostRef.current = host;
      hostNameRef.current = host.name;
      await host.init({ onClickToDial: (contact) => { void doDial(contact); } });
    })();
    return () => {
      disposed = true;
      if (wrapTimer.current) clearTimeout(wrapTimer.current);
      providerRef.current?.dispose();
      hostRef.current?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist call history (per agent) whenever it changes, so a refresh restores
  // it. Only while signed in; logout leaves the stored copy for the next sign-in.
  useEffect(() => {
    const agent = state.auth.agent;
    if (agent && state.auth.status === "loggedIn") saveHistory(agent.id, state.history);
  }, [state.history, state.auth.agent, state.auth.status]);

  // Persist browser-side preferences (auto-answer, ringtone, notifications, DND).
  useEffect(() => { saveSettings(settings); }, [settings]);

  // Apply the selected mic/speaker to any live call the moment it changes.
  useEffect(() => { void providerRef.current?.setAudioDevices?.(); }, [settings.micDeviceId, settings.speakerDeviceId]);

  // Alerting behaviour for an incoming call, driven by the settings above:
  // DND declines it outright; otherwise ring / notify / auto-answer as configured.
  useEffect(() => {
    if (state.status !== "incoming" || !state.call) return;
    const call = state.call;

    if (settings.dnd) { void providerRef.current?.hangup(call.id); return; }

    const cleanups: Array<() => void> = [];
    if (settings.ringtone) { ringtone.start(); cleanups.push(() => ringtone.stop()); }
    if (settings.notifications && "Notification" in window && Notification.permission === "granted") {
      const body = call.contact.name ? `${call.contact.name} · ${call.contact.number}` : call.contact.number;
      const note = new Notification("Incoming call", { body, tag: "ccv2-cti-incoming" });
      note.onclick = () => { window.focus(); note.close(); };
      cleanups.push(() => note.close());
    }
    if (settings.autoAnswer) {
      const t = window.setTimeout(() => {
        if (stateRef.current.status === "incoming") void providerRef.current?.answer(call.id);
      }, 1200);
      cleanups.push(() => clearTimeout(t));
    }
    return () => cleanups.forEach((fn) => fn());
  }, [state.status, state.call, settings.dnd, settings.ringtone, settings.notifications, settings.autoAnswer]);

  const login = useCallback(async (username: string, password: string) => {
    dispatch({ type: "LOGIN_START" });
    try {
      const agent = await authLogin(username, password);
      const cfg = cfgRef.current ?? (await loadConfig());
      const provider: TelephonyProvider = cfg.provider === "livekit"
        ? new LiveKitTelephonyProvider({ ...cfg.livekit, agentIdentity: agent.id })
        : new DemoTelephonyProvider();
      providerRef.current?.dispose();
      providerRef.current = provider;
      await provider.init(buildEvents());
      dispatch({ type: "LOGIN_OK", agent });
      // Restore this agent's saved call history so the Calls/Contacts tabs aren't
      // empty after a page refresh (RingCX-style persistence, scoped per agent).
      dispatch({ type: "LOAD_HISTORY", history: loadHistory(agent.id) });
    } catch (e) {
      dispatch({ type: "LOGIN_ERR", message: e instanceof Error ? e.message : "Sign-in failed." });
    }
  }, [buildEvents]);

  const logout = useCallback(() => {
    providerRef.current?.dispose();
    providerRef.current = null;
    dispatch({ type: "LOGOUT" });
  }, []);

  const api = useMemo<CallApi>(() => ({
    state,
    hostName: hostNameRef.current,
    login,
    logout,
    dial: doDial,
    answer: () => { const c = stateRef.current.call; if (c) void providerRef.current?.answer(c.id); },
    hangup: () => { const c = stateRef.current.call; if (c) void providerRef.current?.hangup(c.id); },
    toggleMute: () => { const c = stateRef.current.call; if (c) { void providerRef.current?.mute(c.id, !c.muted); dispatch({ type: "TOGGLE_MUTE" }); } },
    toggleHold: () => { const c = stateRef.current.call; if (c) { void providerRef.current?.hold(c.id, !c.onHold); dispatch({ type: "TOGGLE_HOLD" }); } },
    toggleRecording: () => { const c = stateRef.current.call; if (c) { void providerRef.current?.setRecording(c.id, !c.recording); dispatch({ type: "TOGGLE_RECORDING" }); } },
    transfer: (target, kind, mode) => { const c = stateRef.current.call; if (c) void providerRef.current?.transfer?.(c.id, target, kind, mode); },
    conference: (participant) => { const c = stateRef.current.call; if (c) void providerRef.current?.conference?.(c.id, participant); },
    sendDigit: (d) => { const c = stateRef.current.call; if (c) void providerRef.current?.sendDigit(c.id, d); },
    setNotes: (notes) => dispatch({ type: "SET_NOTES", notes }),
    setDisposition: (d) => dispatch({ type: "SET_DISPOSITION", disposition: d }),
    setAgentStatus: (s) => { providerRef.current?.setAvailable?.(s === "available"); dispatch({ type: "SET_AGENT_STATUS", status: s }); },
    settings,
    setSetting: (key, value) => setSettings((s) => ({ ...s, [key]: value })),
    clear: () => dispatch({ type: "CLEAR" }),
  }), [state, settings, doDial, login, logout]);

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useCall(): CallApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCall must be used within CallProvider");
  return v;
}
